import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import {
  MANIFEST_REVIEW_DISCREPANCY_FIELDS,
  type ManifestReviewDiscrepancyField,
} from "encamp-shared/src/waste";
import { WasteManifestSource } from "generated-graphql/graphql";

/**
 * Bridges the backend `manifestReviewFieldDiffs` verdicts to the review form's
 * field names, so the manifest review dialog can drop a "sources disagree"
 * indicator next to the right input.
 *
 * The backend returns diffs keyed by the comparators' discrepancy field names
 * (e.g. "generatorStreetAddress"), which do NOT all match the review form's
 * field names (e.g. the generator street lives in the SITE address input
 * `generatorLocationStreet1`, not the mailing input). `REVIEW_FORM_FIELD`
 * reconciles the two namespaces for EVERY field a comparator can emit.
 */

/** One field's diff verdict, adapted for the review form's indicators. */
export interface ReviewFieldDiff {
  /** True when the field's sources disagree (document or vendor vs e-Manifest). */
  discrepant: boolean;
  /** Per-source values for the SourcesDisagreeTooltip; absent sources omitted. */
  values: Partial<Record<WasteManifestSource, string>>;
}

/** Structural shape of a `ManifestReviewFieldDiff` from the query. */
export interface RawReviewFieldDiff {
  field: string;
  wasteLineNumber?: string | null;
  discrepant: boolean;
  eManifest?: string | null;
  vendor?: string | null;
  document?: string | null;
}

/**
 * Every discrepancy field a comparator can emit (the shared
 * `MANIFEST_REVIEW_DISCREPANCY_FIELDS`) -> its review-form field name, or `null`
 * when the field is deliberately not surfaced as a per-field indicator.
 *
 * Typed as an EXHAUSTIVE `Record` over the shared field set: adding a new
 * emitted field in encamp-shared without giving it a disposition here fails to
 * compile, so the review form can never silently drop a field the backend
 * sends. (`quantityUnitOfMeasureCode` isn't here because no comparator emits it
 * — it's absent from the shared set, not merely nulled.)
 *
 * Generator SITE address fields compare `generatorLocation*`, so they map to the
 * site-address inputs, not the `generatorMail*` inputs. Per-waste-line fields
 * map to the part after `wasteLines.${index}.`.
 */
const REVIEW_FORM_FIELD: Record<ManifestReviewDiscrepancyField, string | null> = {
  generatorName: "generatorName",
  generatorId: "generatorId",
  generatorStreetAddress: "generatorLocationStreet1",
  generatorStreet2: "generatorLocationStreet2",
  generatorCity: "generatorLocationCity",
  generatorState: "generatorLocationState",
  generatorZip: "generatorLocationZip",
  shippedDate: "shippedDate",
  destinationFacilityId: "desFacilityId",
  // No single review input for a whole-manifest line-count mismatch.
  wasteLinesCount: null,
  quantity: "quantity",
  containerTypeCode: "containerTypeCode",
  managementMethodCode: "managementMethodCode",
  federalWasteCodes: "federalWasteCodes",
  stateWasteCodes: "stateWasteCodes",
  dotPrintedInformation: "dotPrintedInformation",
  nonHazWasteDescription: "nonHazWasteDescription",
  // Whole-line presence diff / single-source advisory — not field-level inputs.
  unpairedWasteLine: null,
  wasteCodeDotConsistency: null,
};

const REVIEW_DISCREPANCY_FIELD_SET: ReadonlySet<string> = new Set(
  MANIFEST_REVIEW_DISCREPANCY_FIELDS
);

/** Narrows an arbitrary backend field name to a known emitted discrepancy field. */
function isReviewDiscrepancyField(field: string): field is ManifestReviewDiscrepancyField {
  return REVIEW_DISCREPANCY_FIELD_SET.has(field);
}

/**
 * The review-form field name a diff maps to, or `null` when the field is
 * unknown or deliberately not surfaced.
 */
function reviewFormField(field: string): string | null {
  return isReviewDiscrepancyField(field) ? REVIEW_FORM_FIELD[field] : null;
}

/** Composite key for a per-line lookup: `${wasteLineNumber}:${reviewFieldName}`. */
export function wasteLineDiffKey(wasteLineNumber: string, reviewFieldName: string): string {
  return `${wasteLineNumber}:${reviewFieldName}`;
}

/**
 * Discrepancy fields whose values are dates. The comparators stringify dates as
 * full ISO timestamps (e.g. "2025-11-04T00:00:00.000Z"); we render them as a
 * readable calendar date in the tooltip instead.
 */
const DATE_DISCREPANCY_FIELDS = new Set(["shippedDate"]);

/**
 * Discrepancy fields whose values are lists. The comparators stringify these as
 * a JSON array (e.g. `["D001","D002"]`); we render them as a readable
 * comma-separated list in the tooltip instead of the raw bracketed form.
 */
const LIST_DISCREPANCY_FIELDS = new Set(["federalWasteCodes", "stateWasteCodes"]);

/** Render a JSON-array-encoded value as a comma-separated list; pass through otherwise. */
function formatCodeList(value: string): string {
  try {
    const parsed: unknown = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.filter((v) => v != null && v !== "").join(", ");
    }
  } catch {
    // Not JSON (e.g. already a plain string) — fall through and show as-is.
  }
  return value;
}

/** Display form of one source's value: readable dates and comma-joined code lists. */
function displayValue(field: string, value: string | null | undefined): string | null {
  if (value == null || value === "") return null;
  if (DATE_DISCREPANCY_FIELDS.has(field) && !Number.isNaN(new Date(value).getTime())) {
    // UTC + "med" ("MMM d, yyyy"), matching how the comparator compares dates
    // (by calendar day) and how the dialog renders dates elsewhere.
    return getUtcDisplayDate(value, "med");
  }
  if (LIST_DISCREPANCY_FIELDS.has(field)) {
    return formatCodeList(value);
  }
  return value;
}

function toValues(diff: RawReviewFieldDiff): Partial<Record<WasteManifestSource, string>> {
  const values: Partial<Record<WasteManifestSource, string>> = {};
  const eManifest = displayValue(diff.field, diff.eManifest);
  const vendor = displayValue(diff.field, diff.vendor);
  const document = displayValue(diff.field, diff.document);
  if (eManifest != null) values[WasteManifestSource.EManifest] = eManifest;
  if (vendor != null) values[WasteManifestSource.Vendor] = vendor;
  if (document != null) values[WasteManifestSource.ManifestDocument] = document;
  return values;
}

export interface ReviewFieldDiffIndex {
  /** Manifest-level diffs keyed by review form field name. */
  manifestFieldDiffs: Map<string, ReviewFieldDiff>;
  /** Waste-line diffs keyed by `wasteLineDiffKey(wasteLineNumber, reviewField)`. */
  wasteLineFieldDiffs: Map<string, ReviewFieldDiff>;
}

/**
 * Index raw query diffs by the review form's field names. Diffs whose field or
 * waste-line mapping is unknown are dropped (no indicator).
 */
export function indexReviewFieldDiffs(diffs: readonly RawReviewFieldDiff[]): ReviewFieldDiffIndex {
  const manifestFieldDiffs = new Map<string, ReviewFieldDiff>();
  const wasteLineFieldDiffs = new Map<string, ReviewFieldDiff>();

  for (const diff of diffs) {
    if (!diff.discrepant) continue;
    const formField = reviewFormField(diff.field);
    if (formField == null) continue; // unknown field, or one we deliberately don't surface
    const entry: ReviewFieldDiff = { discrepant: true, values: toValues(diff) };

    if (diff.wasteLineNumber != null) {
      wasteLineFieldDiffs.set(wasteLineDiffKey(diff.wasteLineNumber, formField), entry);
    } else {
      manifestFieldDiffs.set(formField, entry);
    }
  }

  return { manifestFieldDiffs, wasteLineFieldDiffs };
}
